/**
 * The item card (`specs/M1-B7-item-card-and-updates.md`).
 *
 * Measured: a **700 × 812 side panel at (740, 88)** — the board stays mounted behind it, and
 * `/boards/<b>/pulses/<i>` renders this same panel; there is no separate full-page route
 * `[ic§How the card opens]`. Close is at the panel's top-left, the tab strip is 32 px high and
 * `Updates` carries the live count.
 */
import { Icon, icons } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onEvent } from '../../lib/realtime/socket';
import {
  applyUpdateEvent,
  insertUpdate,
  removeUpdate,
  replaceUpdate,
  setPinnedLocally,
  toggleLikeLocally,
} from './realtime';
import { ActivityRows } from './ActivityRows';
import { CardMenu } from './CardMenu';
import { raiseCardToast } from './CardToast';
import { Composer } from './Composer';
import { UpdateCard } from './UpdateCard';
import { fileSize, orderUpdates, relativeTime } from './composer-model';
import type {
  CardActivityRow,
  CardAsset,
  CardPerson,
  CardSubitem,
  CardUpdate,
  ItemCardHeader,
  ItemCardSource,
} from './types';
import './item-card.css';

export type ItemCardTab = 'updates' | 'files' | 'activity' | 'info';

export const ITEM_CARD_TABS: { id: ItemCardTab; label: string }[] = [
  { id: 'updates', label: 'Updates' },
  { id: 'files', label: 'Files' },
  { id: 'activity', label: 'Activity Log' },
  { id: 'info', label: 'Info Boxes' },
];

export interface ItemCardProps {
  source: ItemCardSource;
  itemId: string;
  initialTab?: ItemCardTab;
  onClose(): void;
}

export function ItemCard({ source, itemId, initialTab = 'updates', onClose }: ItemCardProps) {
  const [tab, setTab] = useState<ItemCardTab>(initialTab);
  /*
   * **P-266.** The card is one mounted panel for whichever item the route names, so its tab
   * outlived the item: opening row B's conversation while row A's card sat on `Files` showed
   * B's files. A gesture that names a tab — the row's conversation bubble names Updates —
   * must land on it, and opening a *different* item always restarts at the item's own feed,
   * which is what the tab strip's default already says the card opens on.
   */
  useEffect(() => {
    setTab(initialTab);
  }, [itemId, initialTab]);
  const [header, setHeader] = useState<ItemCardHeader | null>(null);
  const [updates, setUpdates] = useState<CardUpdate[]>([]);
  const [assets, setAssets] = useState<CardAsset[]>([]);
  const [activity, setActivity] = useState<CardActivityRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** P-162: the parent's subitems, the same `items.subitemsOf` the grid's nested rows call. */
  const [subitems, setSubitems] = useState<CardSubitem[]>([]);
  /**
   * **P-308.** The api's own sentence when the subitems read is refused — on the pilot,
   * *"Board 6061227186 not found"* from a board whose subitems board belonged to another board
   * (P-307). The card used to swallow it (`catch { setSubitems([]) }`), so an unreachable
   * subitems board read as "no subitems yet"; it now says so, quietly, and the card still opens.
   */
  const [subitemsError, setSubitemsError] = useState<string | null>(null);
  const [addingSubitem, setAddingSubitem] = useState(false);
  /**
   * **P-304, the card's half.** The row whose `⋯` is open. Nico works from the Tasks page,
   * which opens the item card on the board route, and in the card a subitem was a **link and
   * nothing else**: there was no way to delete one from there either.
   */
  const [subitemMenuFor, setSubitemMenuFor] = useState<string | null>(null);
  const [subitemDraft, setSubitemDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const me = source.me();

  const reload = useCallback(async () => {
    try {
      const [nextHeader, nextUpdates] = await Promise.all([source.header(itemId), source.updates(itemId)]);
      setHeader(nextHeader);
      setUpdates(Array.isArray(nextUpdates) ? nextUpdates : []);
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, [source, itemId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /*
   * **Realtime patching, round 5o** (`realtime.ts`). Round 5 reloaded the whole list after
   * every write and never saw anybody else's, because the events were emitted and nothing
   * applied them. The subscription is live for as long as the card is open; the reducer is
   * idempotent, so an event for a write we made ourselves changes nothing.
   *
   * The socket rooms are joined by the board view behind the card (`useBoardRealtime`), so
   * this only has to listen — it never joins or leaves a room of its own.
   */
  const peopleRef = useRef<Map<string, CardPerson>>(new Map());
  useEffect(() => {
    const index = new Map<string, CardPerson>();
    if (me) index.set(String(me.id), me);
    for (const update of updates) {
      if (update.creator) index.set(String(update.creator.id), update.creator);
      for (const reply of update.replies) {
        if (reply.creator) index.set(String(reply.creator.id), reply.creator);
      }
    }
    peopleRef.current = index;
  }, [updates, me]);

  /**
   * **P-166 — the header repaints too.** The card used to subscribe to `update.created` /
   * `update.deleted` and nothing else, so an item renamed anywhere else — the grid behind the
   * card, a second tab, the API, an automation — left the open card showing the old name until
   * it was closed and reopened: two people on one item, and the one with the card open was the
   * one who was wrong. `items.rename` emits `item.updated`, which the board table already
   * consumes for its rows; the card now consumes it for its header.
   *
   * `reload()` re-reads the header **and** the feed and is idempotent, so an event for a write
   * we made ourselves changes nothing. It is held in a ref because it changes identity with
   * `source`/`itemId` and re-subscribing the socket on every render is not free.
   */
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  useEffect(() => {
    return onEvent((event) => {
      if (event.type === 'update.created' || event.type === 'update.deleted') {
        setUpdates((current) => {
          const next = applyUpdateEvent(current, event, {
            me,
            itemId,
            resolvePerson: (userId) => peopleRef.current.get(userId) ?? null,
          });
          return next === current ? current : [...next];
        });
        return;
      }

      if (!event.type.startsWith('item.')) return;
      // `item.deleted` carries the set it removed in the payload; the others carry `itemId`.
      const removed = Array.isArray((event.payload as { itemIds?: unknown[] } | null)?.itemIds)
        ? ((event.payload as { itemIds: unknown[] }).itemIds ?? []).map(String)
        : [];
      if (event.type === 'item.deleted' && removed.includes(itemId)) {
        onClose();
        return;
      }
      if (event.itemId === null || String(event.itemId) !== itemId) return;
      if (event.type === 'item.moved' && (event.payload as { leftBoard?: boolean } | null)?.leftBoard === true) {
        // The item is no longer on the board this card was opened from — the card's own menu
        // closes the panel for exactly this case (`onGone`).
        onClose();
        return;
      }
      void reloadRef.current();
    });
  }, [itemId, me, onClose]);

  useEffect(() => {
    if (tab !== 'files' || !header) return;
    void source
      .assets(itemId)
      .then((rows) => setAssets(Array.isArray(rows) ? rows : []))
      .catch(() => setAssets([]));
  }, [tab, source, itemId, header]);

  /**
   * **P-162 — the subitems surface.** On a phone the card *is* the item (P-062), so with no
   * surface here a subitem could not be reached from its parent at all. The data is the one
   * call the grid already makes; a board that has never had a subitem answers an empty list
   * rather than an error `[sub§2]`, and a subitem's own card shows no section (one level).
   */
  const loadSubitems = useCallback(async () => {
    if (!header || header.parentItemId) {
      setSubitems([]);
      setSubitemsError(null);
      return;
    }
    try {
      setSubitems(await source.subitemsOf({ boardId: header.boardId, itemId }));
      setSubitemsError(null);
    } catch (cause) {
      // **P-308**: the card loads; the section says the sentence instead of the list.
      setSubitems([]);
      setSubitemsError(
        cause instanceof Error && cause.message.trim() !== ''
          ? cause.message
          : 'Subitems could not be loaded',
      );
    }
  }, [source, header, itemId]);

  useEffect(() => {
    void loadSubitems();
  }, [loadSubitems]);

  /**
   * **P-304 — deleting a subitem from the card.**
   *
   * `items.remove` is the **single-item** procedure the card's own *Delete* uses, and it
   * resolves the row's board itself — a subitem lives on the subitems board `[S1§2]`, so the
   * one call is right for both and no board id is passed from here. The toast is the card's
   * own (P-164), raising the same wording and the same working **Undo** the grid raises, and
   * the Undo is the inverse call (`items.restore`, marked `is_undo_action`).
   *
   * monday itself has **no subitems section on the item card at all** — re-checked on the
   * sandbox for this round, `discovery/ui-reference/subitems/15-card-subitems-section.json`:
   * the card is `Updates · Files · Activity Log · Info Boxes` and nothing else. Ours exists
   * because on a phone the card *is* the item (P-162/P-062), so the row's menu is built from
   * the one subitem menu monday **does** have — the grid's, `[uis:05-subitem-row-menu]` —
   * and is kept to the two entries that section can honour (q545).
   */
  const deleteSubitem = useCallback(
    async (subitem: CardSubitem) => {
      setSubitemMenuFor(null);
      try {
        await source.deleteItem({ itemId: subitem.id });
      } catch (cause) {
        raiseCardToast({ message: (cause as Error).message, kind: 'error' });
        return;
      }
      // The card's list and the `Subitems / n` count are read back from the server, never guessed.
      await loadSubitems();
      raiseCardToast({
        message: 'Deleted 1 subitem',
        kind: 'info',
        actionLabel: 'Undo',
        onAction: async () => {
          await source.restoreItem({ itemId: subitem.id });
          await loadSubitems();
        },
      });
    },
    [source, loadSubitems],
  );

  useEffect(() => {
    if (tab !== 'activity' || !header) return;
    void source
      .activity({ boardId: header.boardId, itemIds: [itemId], limit: 50 })
      .then((result) => setActivity(Array.isArray(result?.rows) ? result.rows : []))
      .catch(() => setActivity([]));
  }, [tab, source, itemId, header]);

  const [mailOpen, setMailOpen] = useState(false);
  /** Whether the `…` menu (or its move dialog) is the card's topmost layer — P-167. */
  const [menuOpen, setMenuOpen] = useState(false);


  /**
   * **P-060**: the name renames in place. It was a plain `<h1>`, so the one thing every item
   * surface can do — change its name — was the one thing the card could not.
   */
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');

  /**
   * **Escape closes exactly one layer at a time, topmost first** (F4 §P5, P-167).
   *
   * This listener is on `window` because the key has to close the card from anywhere on the
   * screen behind it — which is precisely why it used to close the card *and* whatever the card
   * itself had open: `Update via email` vanished together with the panel behind it, dropping
   * the user back on the board. The listener now consults the card's own layers and is a no-op
   * while one of them is up; each layer closes itself (the mail dialog here, the rename editor
   * and the composer's popups with `stopPropagation` of their own).
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (mailOpen) {
        setMailOpen(false);
        return;
      }
      // The rename editor and the `…` menu take the key themselves; the card stays put.
      if (renaming || menuOpen) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mailOpen, menuOpen, renaming]);

  const startRename = useCallback(() => {
    setDraft(header?.name ?? '');
    setRenaming(true);
  }, [header?.name]);

  const commitRename = useCallback(async () => {
    const next = draft.trim();
    const previous = header?.name ?? '';
    setRenaming(false);
    // An empty name is not a name, and monday logs no rename that changed nothing.
    if (next === '' || next === previous) return;
    try {
      await source.renameItem({ itemId, name: next });
      /*
       * **P-164.** The same action from the grid's row menu toasts with a working Undo; from
       * the card it used to happen in silence. The inverse of a rename is the rename back —
       * one `items.rename`, which writes its own `update_name` row like any other.
       */
      raiseCardToast({
        message: `Renamed to "${next}"`,
        kind: 'info',
        actionLabel: 'Undo',
        onAction: async () => {
          await source.renameItem({ itemId, name: previous });
          await reload();
        },
      });
    } catch (cause) {
      setError((cause as Error).message);
    }
    await reload();
  }, [draft, header?.name, source, itemId, reload]);

  /**
   * **P-299 — link every pending attachment to the update that has just been posted.**
   *
   * An image pasted into the composer is uploaded the moment it is pasted, so it is an item
   * asset until there is an update to hang it on; `updates.attachAsset` is the same service
   * call `POST /files/upload` makes when the upload *can* name its update (B7 §7), so both
   * orders write the same row, the same `origin` and the same event. A link that is refused
   * toasts the api's sentence and the update still stands — it is posted either way.
   */
  const attachAll = useCallback(
    async (updateId: string, attachments: readonly CardAsset[]): Promise<CardAsset[]> => {
      const done: CardAsset[] = [];
      for (const asset of attachments) {
        try {
          await source.attachAsset({ updateId, assetId: asset.id });
          done.push({ ...asset, origin: 'update' });
        } catch (cause) {
          raiseCardToast({ message: (cause as Error).message, kind: 'error' });
        }
      }
      return done;
    },
    [source],
  );

  const ordered = useMemo(() => orderUpdates(updates), [updates]);
  const count = updates.length;

  return (
    <aside className="ez-item-card" data-testid="item-card" aria-label={header?.name ?? 'Item'}>
      <header className="ez-item-card__header">
        <button type="button" className="ez-item-card__close" aria-label="Close" data-testid="item-card-close" onClick={onClose}>
          ✕
        </button>
        {/*
          * The name is the rename surface: a double-click (the grid's own gesture) or the
          * menu's `Rename` row opens the editor in place, Enter commits it through
          * `items.rename` and Escape abandons it (P-060).
          */}
        <h1
          className="ez-item-card__name"
          data-testid="item-card-name"
          onDoubleClick={startRename}
        >
          {renaming ? (
            <input
              className="ez-item-card__name-input"
              aria-label="Item name"
              data-testid="item-card-name-input"
              value={draft}
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void commitRename();
                }
                if (event.key === 'Escape') {
                  // The card's own Escape closes the panel; a live editor takes it first.
                  event.stopPropagation();
                  setRenaming(false);
                }
              }}
              onBlur={() => void commitRename()}
            />
          ) : (
            (header?.name ?? '…')
          )}
        </h1>
        {header ? (
          <CardMenu
            source={source}
            itemId={itemId}
            boardId={header.boardId}
            name={header.name}
            currentGroupId={header.groupTitle}
            onRename={startRename}
            onGone={onClose}
            onChanged={() => void reload()}
            /* P-164: the card's own toast host, raised with the grid's wording and its Undo. */
            onToast={raiseCardToast}
            /* P-167: while the menu (or its move dialog) is up, Escape closes that, not the card. */
            onOpenChange={setMenuOpen}
          />
        ) : null}
      </header>

      {/*
        * P-029: monday's strip is `⌂ Updates / n · Files · Activity Log · <view> · +`. The
        * home glyph belongs to the **Updates** tab (it is the item's home feed) and the `+`
        * closes the strip `[ic§Card chrome]`.
        */}
      <nav className="ez-item-card__tabs" role="tablist" data-testid="item-card-tabs">
        {ITEM_CARD_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            className={`ez-item-card__tab${tab === entry.id ? ' is-active' : ''}`}
            data-testid={`item-card-tab-${entry.id}`}
            onClick={() => setTab(entry.id)}
          >
            {entry.id === 'updates' ? (
              <Icon icon={icons.home} size={16} ignoreFocusStyle />
            ) : null}
            {/* monday puts the count in the tab label: `Updates / 5` `[ic§Card chrome]`. */}
            {entry.id === 'updates' ? `Updates / ${String(count)}` : entry.label}
          </button>
        ))}
        <button
          type="button"
          className="ez-item-card__tab-add"
          data-testid="item-card-tab-add"
          aria-label="Add a view to this item"
          /* monday's `+` adds an item-card view (an app tab). This product has no item-card
             view registry yet, so the control is present and refuses rather than lying. */
          disabled
          title="Item-card views arrive with M7"
        >
          <Icon icon={icons.add} size={16} ignoreFocusStyle />
        </button>
      </nav>

      {error ? (
        <p className="ez-item-card__error" role="alert" data-testid="item-card-error">
          {error}
        </p>
      ) : null}

      <div className="ez-item-card__body" data-testid={`item-card-body-${tab}`}>
        {tab === 'updates' && header ? (
          <>
            {/*
              * q255, measured: the Updates tab has its own 32 px strip at y 229 carrying
              * **`Update via email` 144 × 32 @ (756, 229)** and `Give feedback` 130 × 32 @
              * (902, 229) in `rgb(103,104,121)` `[ic§q255-updates-header]`. `Give feedback`
              * is monday's own feedback widget and is deliberately not built.
              */}
            <div className="ez-item-card__mail-row" data-testid="item-card-mail-row">
              <button
                type="button"
                className="ez-item-card__mail-link"
                data-testid="update-via-email"
                aria-haspopup="dialog"
                aria-expanded={mailOpen}
                onClick={() => setMailOpen(true)}
              >
                <Icon icon={icons.email} size={16} ignoreFocusStyle />
                Update via email
              </button>
              <span className="ez-item-card__mail-divider" aria-hidden="true" />
              {/*
                * P-029: monday's row carries a second control. `Give feedback` opens monday's
                * own feedback widget, which this product does not have — B1 §3.2 would leave
                * it out entirely, but the parity card asks for the row's shape, so it renders
                * and says why instead of pretending to work.
                */}
              <button
                type="button"
                className="ez-item-card__mail-link ez-item-card__mail-link--feedback"
                data-testid="give-feedback"
                disabled
                title="Feedback is monday's own widget; this product has no feedback channel"
              >
                <Icon icon={icons.feedback} size={16} ignoreFocusStyle />
                Give feedback
              </button>
            </div>
            {mailOpen ? <UpdateViaEmailDialog itemId={itemId} onClose={() => setMailOpen(false)} /> : null}

            {/*
              * **P-162 — Subitems.** The tab strip was `Updates / n · Files · Activity Log ·
              * Info Boxes` and nothing else, on an item whose grid row shows a subitem count;
              * on a phone the card *is* the item (P-062), so a subitem could not be reached
              * from its parent at all.
              *
              * **ASSUMED (q545):** monday's placement of the section on the card was never
              * captured (`discovery/ui-reference/item-card/**` has the four tabs and no
              * subitems surface), so this is the conservative reading of P-162 — the list
              * **under the Updates feed's header**, not a fifth tab that would claim a tab
              * strip we have not measured. Each row opens **that subitem's** card, which is
              * an ordinary item card `[S1§2]`, and `+ Add subitem` is `items.createSubitem`.
              */}
            {header.parentItemId ? null : (
              <section
                className={
                  subitems.length === 0 && subitemsError === null
                    ? 'ez-subitems ez-subitems--bare'
                    : 'ez-subitems'
                }
                data-testid="item-card-subitems"
                data-empty={subitems.length === 0 && subitemsError === null ? 'true' : undefined}
              >
                {/*
                  * **P-309** (Nico: *"is it possible to cancel this 'subitems no yet' if there
                  * are no subitems?"*). monday's item card has **no Subitems section at all**
                  * (`discovery/ui-reference/subitems/15-card-subitems-section.json`, q589); ours
                  * exists only because on a phone the card *is* the item (P-162/P-062).
                  *
                  * **ASSUMED (P-309):** with nothing to show, the section collapses to the one
                  * thing that still has a job — a quiet `+ Add subitem` link under the card
                  * tabs. No box, no heading, no *"No subitems yet"* sentence, on every width.
                  * With subitems, or with something to report (P-308), it is the box as before.
                  */}
                {subitems.length === 0 && subitemsError === null ? null : (
                  <h2 className="ez-subitems__title">
                    Subitems{subitems.length > 0 ? ` / ${String(subitems.length)}` : ''}
                  </h2>
                )}
                {subitemsError === null ? null : (
                  /* **P-308**: the api's own sentence, never a rewrite of it. */
                  <p
                    className="ez-subitems__unavailable"
                    data-testid="item-card-subitems-unavailable"
                    title={subitemsError}
                  >
                    Subitems unavailable
                  </p>
                )}
                {subitems.length === 0 ? null : (
                  <ul className="ez-subitems__list">
                    {subitems.map((subitem) => (
                      <li key={subitem.id} className="ez-subitems__row">
                        <a
                          className="ez-subitems__name"
                          data-testid={`subitem-${subitem.id}`}
                          href={`/boards/${subitem.boardId}/pulses/${subitem.id}`}
                        >
                          {subitem.name}
                        </a>
                        {/*
                          * **P-304.** The row's own `⋯`. Its two entries are the captured
                          * subitem menu's own wording (`[uis:05-subitem-row-menu]`): *Open
                          * subitem* is what the link already did, *Delete* is what the card
                          * had no path to at all. It is a 44 px target, because on a phone
                          * this is the only subitems surface there is (P-062).
                          */}
                        <div className="ez-subitems__actions">
                          <button
                            type="button"
                            className="ez-subitems__menu-button"
                            data-testid={`subitem-menu-${subitem.id}`}
                            aria-label={`More options for ${subitem.name}`}
                            aria-haspopup="menu"
                            aria-expanded={subitemMenuFor === subitem.id}
                            onClick={() =>
                              setSubitemMenuFor((current) => (current === subitem.id ? null : subitem.id))
                            }
                          >
                            ···
                          </button>
                          {subitemMenuFor === subitem.id ? (
                            <div
                              className="ez-subitems__menu"
                              role="menu"
                              aria-label={`Subitem ${subitem.name}`}
                              data-testid={`subitem-menu-panel-${subitem.id}`}
                              onKeyDown={(event) => {
                                if (event.key !== 'Escape') return;
                                // One layer at a time (P5): the menu closes, the card stays put.
                                event.stopPropagation();
                                setSubitemMenuFor(null);
                              }}
                            >
                              <button
                                type="button"
                                role="menuitem"
                                className="ez-subitems__menu-item"
                                data-testid={`subitem-open-${subitem.id}`}
                                onClick={() => {
                                  setSubitemMenuFor(null);
                                  window.location.assign(`/boards/${subitem.boardId}/pulses/${subitem.id}`);
                                }}
                              >
                                Open subitem
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="ez-subitems__menu-item ez-subitems__menu-item--danger"
                                data-testid={`subitem-delete-${subitem.id}`}
                                onClick={() => void deleteSubitem(subitem)}
                              >
                                Delete
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {addingSubitem ? (
                  <input
                    className="ez-subitems__input"
                    aria-label="Subitem name"
                    data-testid="item-card-subitem-input"
                    value={subitemDraft}
                    autoFocus
                    onChange={(event) => setSubitemDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        // One layer at a time (P5): the editor closes, the card stays put.
                        event.stopPropagation();
                        setAddingSubitem(false);
                        setSubitemDraft('');
                      }
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      const name = subitemDraft.trim();
                      setAddingSubitem(false);
                      setSubitemDraft('');
                      if (name === '') return;
                      void source
                        .createSubitem({ parentItemId: itemId, name })
                        .then(() => loadSubitems())
                        /*
                         * **P-308.** `setError` is the card's *panel* error — it replaces the
                         * whole card body, which is exactly the "one refused subitem takes the
                         * item down" shape this round removes. The api's own sentence goes to
                         * the card's toast instead and the card stays on screen.
                         */
                        .catch((cause: unknown) =>
                          raiseCardToast({ message: (cause as Error).message, kind: 'error' }),
                        );
                    }}
                    onBlur={() => {
                      setAddingSubitem(false);
                      setSubitemDraft('');
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="ez-subitems__add"
                    data-testid="item-card-add-subitem"
                    onClick={() => setAddingSubitem(true)}
                  >
                    + Add subitem
                  </button>
                )}
              </section>
            )}

            <Composer
              /* P-163: the id the composer writes, written at its call site as well. */
              testId="update-composer"
              variant="update"
              boardId={header.boardId}
              itemId={itemId}
              me={me}
              mentionCandidates={(query) => source.mentionCandidates(query)}
              /*
                * No `reload()` here any more: the write emits `update.created`, and the
                * subscription above patches the list — the same path another person's update
                * takes, so the two can never disagree (B7-A12).
                */
              onSubmit={async ({ body, mentions, attachments }) => {
                const created = await source.createUpdate({ itemId, body, mentions });
                /*
                 * **P-299 — the second half of a paste.** The images were uploaded when they
                 * were pasted (monday's order, measured), so the update they belong to is only
                 * known now; `attachAsset` links each one and the local row carries them
                 * straight away, which is what P-028 paints inline.
                 */
                const attached = await attachAll(created.id, attachments);
                /* P-303: `me` is the author of our own write — an answer with no creator is not "Unknown". */
                setUpdates((current) => insertUpdate(current, { ...created, assets: attached }, me));
              }}
              /* The paperclip's own path, and now the paste's: upload now, post with the update. */
              onAttach={(file) => source.uploadFile({ itemId, file })}
            />
            {ordered.length === 0 ? (
              <p className="ez-item-card__empty" data-testid="updates-empty">
                No updates yet
              </p>
            ) : (
              ordered.map((update) => (
                <UpdateCard
                  key={update.id}
                  update={update}
                  boardId={header.boardId}
                  itemId={itemId}
                  me={me}
                  mentionCandidates={(query) => source.mentionCandidates(query)}
                  /* Reply, edit, delete and like all patch through the event (see above). */
                  onReply={async ({ attachments, ...input }) => {
                    const created = await source.createReply(input);
                    const attached = await attachAll(created.id, attachments);
                    setUpdates((current) => insertUpdate(current, { ...created, assets: attached }, me));
                  }}
                  onEdit={async ({ attachments, ...input }) => {
                    const edited = await source.editUpdate(input);
                    const attached = await attachAll(edited.id, attachments);
                    setUpdates((current) =>
                      replaceUpdate(current, { ...edited, assets: [...edited.assets, ...attached] }, me),
                    );
                  }}
                  onDelete={async (updateId) => {
                    await source.deleteUpdate({ updateId });
                    setUpdates((current) => removeUpdate(current, updateId));
                  }}
                  onToggleLike={async (target) => {
                    const liked = me !== null && target.likes.some((like) => like.userId === me.id);
                    await source.setLiked({ updateId: target.id, liked: !liked });
                    setUpdates((current) => toggleLikeLocally(current, target.id, !liked, me));
                  }}
                  /* `pinned` has no event of its own, so the local patch is the whole story. */
                  onTogglePin={async (target) => {
                    await source.setPinned({ updateId: target.id, pinned: !target.pinned });
                    setUpdates((current) => setPinnedLocally(current, target.id, !target.pinned));
                  }}
                  onAttach={(file) => source.uploadFile({ itemId, file })}
                  /*
                   * P-317: who has seen it. Read on the gesture, not with the page — a card with
                   * forty updates would otherwise pull forty readerships nobody looked at. A
                   * source that does not implement it keeps the counter the plain label it was,
                   * rather than throwing on the first hover.
                   */
                  {...(typeof source.updateReaders === 'function'
                    ? { onReaders: (input: { updateId: string }) => source.updateReaders(input) }
                    : {})}
                />
              ))
            )}
          </>
        ) : null}

        {tab === 'files' ? (
          <section className="ez-files" data-testid="files-tab">
            <div className="ez-files__toolbar">
              {/*
                * **P-165.** `Add file` was chrome: no `onClick`, no input, no error — the one
                * control on the tab did nothing at all. It is the composer's own upload path
                * (`source.uploadFile`), with **no** `updateId`, which is exactly the
                * `origin: "item"` row the list below already renders `[ic§Files tab]`.
                */}
              <button
                type="button"
                className="ez-files__add"
                data-testid="files-add"
                onClick={() => fileInputRef.current?.click()}
              >
                Add file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="ez-files__input"
                data-testid="files-file-input"
                hidden
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  try {
                    await source.uploadFile({ itemId, file });
                    setAssets(await source.assets(itemId));
                  } catch (cause) {
                    setError((cause as Error).message);
                  }
                }}
              />
            </div>
            {assets.length === 0 ? (
              <p className="ez-item-card__empty" data-testid="files-empty">
                No files yet
              </p>
            ) : (
              <ul className="ez-files__list">
                {assets.map((asset) => (
                  <li key={asset.id} className="ez-files__row" data-testid={`file-${asset.id}`}>
                    <a href={asset.url} className="ez-files__name" data-testid={`file-${asset.id}-link`} download>
                      {asset.name}
                    </a>
                    <span className="ez-files__size">{fileSize(asset.size)}</span>
                    {/* monday shows where the file came from as a breadcrumb `[ic§Files tab]`. */}
                    <span className="ez-files__breadcrumb" data-testid={`file-${asset.id}-origin`}>
                      {asset.origin === 'update' ? 'Update' : asset.origin === 'column' ? 'Column' : 'Item'}
                    </span>
                    <span className="ez-files__time">{asset.createdAt ? relativeTime(asset.createdAt) : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {tab === 'activity' ? <ActivityRows rows={activity} testId="item-activity-rows" /> : null}

        {tab === 'info' ? (
          <section className="ez-info-boxes" data-testid="info-boxes-tab">
            {/* The captured empty state: three 143 × 37 choices `[ic§Info Boxes tab]`. */}
            {['Add a note', 'Add files', 'Add Q&A'].map((label) => (
              <button key={label} type="button" className="ez-info-boxes__choice" disabled title="Info boxes arrive with M7">
                {label}
              </button>
            ))}
          </section>
        ) : null}
      </div>

    </aside>
  );
}

/**
 * **`Update via email` (q255), measured on the sandbox.**
 *
 * monday's dialog is **576 × 375 @ (324, 261)**, radius 16, `0 15px 50px rgba(0,0,0,.3)`,
 * headed *"Create updates via email"* over *"Create updates on this item by sending an email
 * to this address."*, then two sections — **Secure address** ("Can be used by people outside
 * the company") carrying `<account>_pulse_<itemId>_<32 hex>__<userId>@use1.mx.monday.com`
 * with a **`Copy` 72 × 32 @ (780, 443)**, and **Custom address** ("Can be used only by
 * account members") `[ic§q255-update-via-email]`.
 *
 * That answers the half of q255 the API could not: the address is **derived per item and per
 * user and is nowhere in the 2026-10 schema** — it is UI-only state, so an importer cannot
 * carry it and an integration cannot read it.
 *
 * **What we build.** The chrome, to the captured geometry, and an honest address field: this
 * product has no inbound mail path (`README-dev.md` — `SMTP_URL` is outbound only, and there
 * is no MX), so inventing an address that silently swallows mail would be worse than saying
 * so. The field reads "not configured" and `Copy` is disabled until an inbound domain exists.
 */
function UpdateViaEmailDialog({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const titleId = `update-via-email-title-${itemId}`;
  return (
    <div className="ez-mail-dialog__scrim" data-testid="update-via-email-scrim" onClick={onClose}>
      <div
        className="ez-mail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="update-via-email-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="ez-mail-dialog__title" id={titleId}>
          Create updates via email
        </h2>
        <p className="ez-mail-dialog__lede">Create updates on this item by sending an email to this address.</p>

        <section className="ez-mail-dialog__section">
          <h3 className="ez-mail-dialog__heading">Secure address</h3>
          <p className="ez-mail-dialog__hint">Can be used by people outside the company</p>
          <div className="ez-mail-dialog__address-row">
            <output className="ez-mail-dialog__address" data-testid="update-via-email-address">
              Email updates are not configured on this account
            </output>
            <button type="button" className="ez-mail-dialog__copy" data-testid="update-via-email-copy" disabled>
              Copy
            </button>
          </div>
        </section>

        <section className="ez-mail-dialog__section">
          <h3 className="ez-mail-dialog__heading">Custom address</h3>
          <p className="ez-mail-dialog__hint">Can be used only by account members</p>
        </section>

        <button type="button" className="ez-mail-dialog__close" aria-label="Close" data-testid="update-via-email-close" onClick={onClose}>
          ✕
        </button>
      </div>
    </div>
  );
}
